import {Component, computed, input, linkedSignal, output} from '@angular/core';
import {AppNumberPipe} from '@frontend/shared';
import {TranslocoPipe} from '@jsverse/transloco';
import {Tooltip} from 'primeng/tooltip';
import {ProvisioningType} from '@frontend/shared-api';
import {OptionsSelectComponent} from '../../../inputs/options-select/options-select.component';
import {SelectOption, SelectValue} from '../../../inputs/options-select/options-select.types';
import {TwoStageAnswerChange} from './attribute-fulfillment-two-stage.types';
import {
  allowedProvisioningTypes,
  CAPABILITY_LEVELS,
  CapabilityLevel,
  reconcileCapabilityLevel,
  reconcileProvisioningType,
  twoStageFactor,
} from './two-stage-answer.utils';

const KEY_PREFIX = 'provider.tenderResponse.twoStage';

@Component({
  selector: 'app-attribute-fulfillment-two-stage',
  imports: [OptionsSelectComponent, TranslocoPipe, AppNumberPipe, Tooltip],
  templateUrl: './attribute-fulfillment-two-stage.component.html',
})
export class AttributeFulfillmentTwoStageComponent {
  readonly capabilityLevel = input.required<number | null>();
  readonly provisioningType = input.required<ProvisioningType | null>();
  readonly singleStageScore = input.required<number | null>();
  readonly disabled = input.required<boolean>();
  readonly readonly = input(false);
  readonly testIdKey = input.required<string>();

  readonly valueChange = output<TwoStageAnswerChange>();

  private readonly selectedCapability = linkedSignal<CapabilityLevel | null>(() =>
    this.toCapabilityLevel(this.capabilityLevel()),
  );
  private readonly selectedProvisioning = linkedSignal<ProvisioningType | null>(() =>
    this.provisioningType(),
  );

  readonly capabilityValue = computed<SelectValue<number>>(
    () => this.selectedCapability() ?? undefined,
  );
  readonly provisioningValue = computed<SelectValue<string>>(
    () => this.selectedProvisioning() ?? undefined,
  );
  readonly capabilityOptions = computed<SelectOption<number>[]>(() =>
    CAPABILITY_LEVELS.map((level) => ({
      directusKey: `${KEY_PREFIX}.capability.level${level}`,
      value: level,
      descriptionKey: `${KEY_PREFIX}.capability.level${level}.description`,
    })),
  );
  readonly provisioningOptions = computed<SelectOption<string>[]>(() =>
    allowedProvisioningTypes(this.selectedCapability()).map((type) => ({
      directusKey: `${KEY_PREFIX}.provisioning.${type}`,
      value: type,
      descriptionKey: `${KEY_PREFIX}.provisioning.${type}.description`,
    })),
  );
  readonly capabilityDescriptionKey = computed(() => {
    const level = this.selectedCapability();
    return level === null ? null : `${KEY_PREFIX}.capability.level${level}.description`;
  });
  readonly provisioningDescriptionKey = computed(() => {
    const type = this.selectedProvisioning();
    return type === null ? null : `${KEY_PREFIX}.provisioning.${type}.description`;
  });
  readonly factor = computed(() =>
    twoStageFactor(this.selectedCapability(), this.selectedProvisioning()),
  );
  readonly showsSingleStageHint = computed(
    () => !this.readonly() && this.singleStageScore() !== null && this.capabilityLevel() === null,
  );
  readonly capabilityLabelKey = computed(() => {
    const level = this.selectedCapability();
    return level === null ? null : `${KEY_PREFIX}.capability.level${level}`;
  });
  readonly provisioningLabelKey = computed(() => {
    const type = this.selectedProvisioning();
    return type === null ? null : `${KEY_PREFIX}.provisioning.${type}`;
  });
  readonly displayedFactor = computed(() => this.factor() ?? this.singleStageScore());

  onCapabilityChange(value: SelectValue<number>): void {
    const level = this.toCapabilityLevel(value ?? null);
    this.selectedCapability.set(level);
    this.selectedProvisioning.set(reconcileProvisioningType(level, this.selectedProvisioning()));
    this.emitWhenComplete();
  }

  onProvisioningChange(value: SelectValue<string>): void {
    const type = this.toProvisioningType(value ?? null);
    this.selectedProvisioning.set(type);
    this.selectedCapability.set(reconcileCapabilityLevel(this.selectedCapability(), type));
    this.emitWhenComplete();
  }

  private emitWhenComplete(): void {
    const capabilityLevel = this.selectedCapability();
    const provisioningType = this.selectedProvisioning();
    const factor = twoStageFactor(capabilityLevel, provisioningType);
    if (capabilityLevel === null || provisioningType === null || factor === null) {
      return;
    }
    this.valueChange.emit({capabilityLevel, provisioningType, factor});
  }

  private toCapabilityLevel(value: number | null): CapabilityLevel | null {
    const level = CAPABILITY_LEVELS.find((candidate) => candidate === value);
    return level ?? null;
  }

  private toProvisioningType(value: string | null): ProvisioningType | null {
    const type = Object.values(ProvisioningType).find((candidate) => candidate === value);
    return type ?? null;
  }
}
